/**
 * Donor-visible decimal places for currency display.
 *
 * Harmonized with classy-checkout (PR-A #477) and classy-embedded-app (PR-B #720) as
 * part of the PRO-1917 epic (2026-05-14): every code in the canonical Stripe zero-decimal
 * list (https://docs.stripe.com/currencies) plus 4 intentional pragmatic divergences
 * appears here with value 0. Unknown codes fall back to 2 via `getCurrencyDecimalPlaces`
 * (`?? 2`).
 *
 * Intentional pragmatic divergences (4):
 *   - COP, IDR: displayed as 0-decimal even though formal ISO 4217 indicates 2-decimal
 *     (donor-side rendering preference).
 *   - ISK: 0-decimal matches ISO 4217 (Iceland abolished the aurar in 2003). Stripe API
 *     requires 2-decimal backward-compat representation; that concern is tracked in
 *     PR-A's PRO-5395 Spike, not handled in PR-C.
 *   - MGA: 0-decimal matches Stripe and most fintech practice. ISO 4217 formally
 *     documents MGA as `1 ariary = 5 iraimbilanja` (non-decimal subdivision).
 *
 * Multi-authority ambiguity:
 *   - CLP: Wikipedia ISO 4217 says exponent 0; Adyen classifies as 2-decimal. This map
 *     follows Stripe's documented list (CLP zero-decimal).
 *
 * Frozen at definition time (PRO-1917 PR-C AR-H2 immutability hardening) so no consumer
 * can silently break the donor-decimal contract by mutating this map. Typed as
 * Readonly<Record> so the static type system enforces immutability at compile time.
 *
 * Exported as a named export for test inspection (immutability assertions in
 * `currency.ai.spec.ts`); production consumers should call `getCurrencyDecimalPlaces`
 * (which normalizes case and applies the `?? 2` fallback).
 */
export const currencyDecimalMap: Readonly<Record<string, number>> = Object.freeze({
  BIF: 0,
  CLP: 0,
  COP: 0,
  DJF: 0,
  GNF: 0,
  IDR: 0,
  ISK: 0,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  MGA: 0,
  PYG: 0,
  RWF: 0,
  UGX: 0,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XOF: 0,
  XPF: 0,
})

export const getCurrencyDecimalPlaces = (currencyCode: string): number => {
  return currencyDecimalMap[currencyCode.toUpperCase()] ?? 2
}
